import { skipToken, useInfiniteQuery } from '@tanstack/react-query';

import { fetchClient } from '@/lib/openapi-fetch/fetchClient';
import type { components } from '@/types/apiSchema';

export const INTERVIEWS_PAGE_SIZE = 3;

type InterviewFilter = Exclude<components['schemas']['InterviewFilter'], 'All'>;

export const interviewsQueryKeys = {
  all: ['interviews'] as const,
  list: (companyId: string | undefined, filter: InterviewFilter) =>
    [...interviewsQueryKeys.all, companyId, filter] as const,
};

export function useInfiniteInterviewsQuery(
  companyId: string | undefined,
  filter: InterviewFilter,
  shouldFetch: boolean,
) {
  const query = useInfiniteQuery({
    queryKey: interviewsQueryKeys.list(companyId, filter),
    initialPageParam: undefined as string | undefined,
    queryFn: shouldFetch
      ? async ({ pageParam }) => {
          const { data } = await fetchClient.GET('/coordination/interviews', {
            params: {
              query: {
                companyId,
                cursor: pageParam,
                filter,
                limit: INTERVIEWS_PAGE_SIZE,
              },
            },
          });

          return data;
        }
      : skipToken,
    getNextPageParam: (lastPage) => lastPage?.nextCursor ?? undefined,
  });

  return {
    ...query,
    interviews: query.data?.pages.flatMap((page) => page?.items ?? []) ?? [],
    totalCount: query.data?.pages[0]?.metadata.totalCount ?? 0,
  };
}
